import { ServiceType } from "@/data/model/Enums";
import type { IService } from "@/data/model/Service";
import { Format } from "@/data/util/Format";

import { clientLanguage } from "@/shared/localization/language";
import { Ripple } from "@/shared/ui/Ripple";

import { TastierClient } from "@/app/TastierClient";
import { BaseController } from "@/app/controller/BaseController";
import { trans } from "@/app/localization/Trans";
import { UserMenu } from "@/app/ui/UserMenu";

export class ServicesController extends BaseController {
    private $head: JQuery;
    private $parallaxHeader?: JQuery;
    private $scrollable: JQuery;
    private $services: JQuery;

    public userMenu: UserMenu;

    public onServiceClicked?: { (service: IService) };

    constructor() {
        super("services");

        this.$head = $('<div class="head"><div class="title"></div></div>').appendTo(this.$view);
        this.userMenu = new UserMenu().appendTo(this.$head);

        this.$scrollable = $('<div class="scrollable" />').appendTo(this.$view);
        this.$services = $('<div class="services" />').appendTo(this.$scrollable);

        this.refreshServices();

        TastierClient.context.onAllocationChange.add(() => {
            this.refreshServices();

            // re-initialize unpaid amount listener
            if (TastierClient.payContext) {
                TastierClient.payContext.onAmountChanged.remove(this.updateUnpaidAmount, this);
                TastierClient.payContext.onAmountChanged.add(this.updateUnpaidAmount, this);
            }
        }, this);
    }

    protected async onBecomeVisible() {
        $(window).bind("resize.services", () => this.resize());

        if (this.$parallaxHeader !== undefined) this.bindParallaxScroll();

        this.$services.find("> .grid > .service").each((i, e) => {
            const aee = $(e).parent();

            aee.delay(i * 90).queue((e) => {
                this.resize(aee.addClass("shown"));
                e();
            });
        });

        if (TastierClient.payContext) {
            TastierClient.payContext.onAmountChanged.add(this.updateUnpaidAmount, this);
        }
        TastierClient.context.onBillChange.add(this.updateUnpaidAmount, this);
        await TastierClient.context.reloadBill();
        this.updateUnpaidAmount();
    }

    protected onBecomeInvisible() {
        $(window).unbind("resize.services");
        this.$services.find("> .grid.shown").removeClass("shown");
        this.unbindParallaxScroll();

        if (TastierClient.payContext) {
            TastierClient.payContext.onAmountChanged.remove(this.updateUnpaidAmount, this);
        }
        TastierClient.context.onBillChange.remove(this.updateUnpaidAmount, this);
    }

    private updateUnpaidAmount() {
        // if no bill element is present - nothing to update, skip
        const $billTotalAmount = this.$services.find(".bill-total-amount");
        if ($billTotalAmount.length === 0) return;

        // if bill is loaded - get unpaid amount
        const unpaidAmount = TastierClient.payContext.getCurrentUnpaidAmount();

        // if there is unpaid amount - display red service icon
        if (unpaidAmount > 0) {
            this.$services.find(".pay-service").addClass("has-open-bill");
        } else {
            this.$services.find(".pay-service").removeClass("has-open-bill");
        }

        // show unpaid amount only to waiters
        if (!TastierClient.identityContext.isWaiterInCurrentLocation()) return;

        $billTotalAmount.text(Format.toMoneyFormat(unpaidAmount));
        $billTotalAmount.addClass("shown");
    }

    private refreshServices() {
        if (this.$parallaxHeader !== undefined) {
            this.$parallaxHeader.remove();
            this.$view.removeClass("hasparallax");
            this.unbindParallaxScroll();
            this.$parallaxHeader = undefined;
        }

        this.$services.empty();

        if (TastierClient.context.currentAllocation === undefined) {
            this.$head.find("> .title").empty();
            return;
        }

        this.$head.find("> .title").html(Format.htmlEscape(TastierClient.context.currentAllocation.locationName)!);
        if (TastierClient.context.currentAllocation.locationDesign.logoLightPath !== undefined) {
            this.$view.addClass("hasparallax");
            this.$parallaxHeader = $('<div class="parallax show"></div>').insertAfter(this.$head);

            this.$parallaxHeader.append(
                '<div class="logo"><img src="' +
                    TastierClient.context.currentAllocation.locationDesign.logoLightPath +
                    '" alt="Logo" /></div>'
            );
            this.bindParallaxScroll();
        }

        if (TastierClient.context.currentAllocation.services.length === 0) return;

        const services = TastierClient.context.currentAllocation.services;
        const lang = clientLanguage();
        let delayIt = 0;

        const sortOrderNumbers = services.map((c) => c.order);
        const maxSortOrderNumber = Math.max(...sortOrderNumbers);

        const self = this;
        for (let i = 0; i <= maxSortOrderNumber; i++) {
            const service = services.find((c) => c.order == i);
            const gridEntry = $('<div class="grid"></div>').appendTo(this.$services);

            if (service === undefined) {
                this.resize(gridEntry.addClass("invis"));
                continue;
            }

            let name: string = Format.translate(service.name, lang);
            if (name === undefined) {
                name = trans.enums.serviceType[ServiceType[service.serviceType]];
            }

            const icon = service.iconClass === undefined ? Format.serviceIcon(service.serviceType) : service.iconClass;
            const serviceName = Format.htmlEscape(name);
            const serviceCard = $(
                `<div class="service">
                    <div class="center">
                        <div class="icon">
                            <i class="${icon}"></i>
                        </div>
                        <div class="lbl">${serviceName}</div>
                    </div>
                </div>`
            ).appendTo(gridEntry);

            if (service.serviceType == ServiceType.Pay) {
                serviceCard.addClass("pay-service");
            }
            if (service.serviceType == ServiceType.Order) {
                serviceCard.addClass("order-service");
            }

            if (service.serviceType == ServiceType.Pay) {
                serviceCard.find(".center").append('<div class="bill-total-amount"></div>');
            }

            Ripple.bind(
                serviceCard.data("model", service).click((e) => {
                    if (this.onServiceClicked !== undefined) {
                        this.onServiceClicked($(e.currentTarget).data("model"));
                    }
                })
            );

            if (this.isVisible()) {
                delayIt++;
                gridEntry.delay(delayIt * 90).queue(function (this: any, e) {
                    self.resize($(this).addClass("shown"));
                    e();
                });
            }
        }
    }

    private resize($el?: JQuery) {
        const ratio = 144 / 188;
        const newHeight = ((this.$services.width() - 12) / 2) * ratio;

        if ($el !== undefined) $el.height(newHeight);
        else {
            this.$services.find("> .grid").each((i, e) => {
                $(e).height(newHeight);
            });
        }
    }

    private bindParallaxScroll() {
        this.$scrollable.unbind("scroll.services").bind("scroll.services", () => {
            const offset = this.$scrollable.scrollTop();
            if (offset > 35) {
                if (this.$parallaxHeader!.hasClass("show")) this.$parallaxHeader!.removeClass("show");
            } else {
                if (!this.$parallaxHeader!.hasClass("show")) this.$parallaxHeader!.addClass("show");
            }
        });
    }

    private unbindParallaxScroll() {
        this.$scrollable.unbind("scroll.services");
    }
}
